import { FC, useMemo } from 'react';
import { useAppSelector } from '../../../../lib/store/hooks';
import { useParams } from 'react-router-dom';
import { Form } from 'antd';
import {
  formatArrangedData,
  getArrangesInitialValues,
  getParsedSelectedItems,
  getSelectedItemsLabels,
} from '../../../../lib/utils/arranges-helpers';
import { useStringArrange } from '../../../../features/table/helpers/useStringArrange';
import { TableHeaderCell } from '../../../../components/Table/TableHeaderCell/TableHeaderCell';
import { ArrangeListPopup } from '../../../../features/table/components/popups/ArrangeListPopup';
import { useCampaignArrangeRequest } from '../../hooks/useCampaignArrangeRequest';
import { useLazyGetCampaignsForFilteringQuery } from '../../../../lib/store/api/campaigns';

type Props = {
  arrangedId: string;
  title: string;
  searchFieldName?: string;
  filterFieldName?: string;
  //eslint-disable-next-line
  headerCellProps?: any;
  isParsedByValue?: boolean;
  disableFiltering?: boolean;
};

export const CampaignSearchDataHeaderCell: FC<Props> = ({
  arrangedId,
  title,
  searchFieldName,
  filterFieldName,
  headerCellProps: additionalHeaderCellProps,
  isParsedByValue,
  disableFiltering,
}) => {
  const sortingAndFiltersData = useAppSelector(({ campaigns }) => campaigns);

  const {
    arrangeId,
    sorting,
    filtering: { filter, filtersWithLabels },
  } = sortingAndFiltersData;

  const [findCampaigns] = useLazyGetCampaignsForFilteringQuery();

  const { id: tenantId, modelId } = useParams();

  const isActiveArrange = arrangeId === arrangedId;

  const [form] = Form.useForm();
  const getSelectedItems = () => getSelectedItemsLabels(form);

  const { filtering } = useStringArrange({
    form,
    request: (value: string) => {
      return findCampaigns({
        search: value,
        searchFieldName: String(searchFieldName),
        tenantId: String(tenantId),
        projectId: String(modelId),
      }).unwrap();
    },
    formatResponse: (data: { content: any[] }) => {
      return formatArrangedData(data, arrangedId);
    },
  });

  const { applyArranges, removeFilter, removeFilterValue } = useCampaignArrangeRequest({
    arrangedId,
    getFilterLabel: () => getSelectedItems().join(', '),
    getFilters: () => ({ [String(filterFieldName)]: getSelectedItems() }),
    form,
    filterFieldName,
    isParsedByValue,
  });

  const filterLabel = useMemo(() => {
    if (isParsedByValue && Array.isArray(filtersWithLabels)) {
      return filtersWithLabels.map((item) => item.label).join(', ');
    }

    return Array.isArray(filter) && filter.join(', ');
  }, [filter, filtersWithLabels, isParsedByValue]);

  const headerCellProps = {
    arrangedId,
    title,
    removeFilter,
    filterLabel: filterLabel,
    sortOrder: sorting.sortOrder,
    isActiveArrange,
    form,
    applyArranges,
    getParsedItems: () => getParsedSelectedItems(form),
    removeFilterValue,
    ...additionalHeaderCellProps,
  };

  return (
    <TableHeaderCell {...headerCellProps}>
      <ArrangeListPopup
        disableFiltering={disableFiltering}
        initialValues={getArrangesInitialValues(form)}
        form={form}
        filtering={filtering}
        onFinish={applyArranges}
        isActiveArrange={isActiveArrange}
      />
    </TableHeaderCell>
  );
};
